import { Box, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { Button } from '@progress/kendo-react-buttons';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Layout } from 'react-grid-layout';
import GridLayout, { type GridItem } from '../../../components/common/GridLayout';
import { DragOutline } from '../../../icons/icons';
import { useEvent } from '../../contexts/event-bus';
import { useAppDispatch, useAppSelector } from '../../hooks/store-hooks';
import { setSetting } from '../../state/slices/settings-slice';
import { mergeWidgets, settingsAreEqual, toWidgetSettings } from '../../utils/dashboard';
import type { SavedDashboardLayout } from './dashboard-custom-layouts';
import {
    applyDashboardLayout,
    DEFAULT_DASHBOARD_LAYOUT,
    getVisibleDashboardWidgets,
    mergeEditedDashboardLayout
} from './dashboard-layout-presets';
import { createDashboardTheme } from './dashboard-theme';
import './Dashboard.css';
import DashboardSettings from './DashboardSettings';
import DashboardSummary from './DashboardSummary';
import { DivertingFlightsList } from './DivertingFlightsList';
import EmergencyFlightsList from './EmergencyFlightsList';
import { FlightStatsChart } from './FlightStatsChart';
import { GoAroundFlightsList } from './GoAroundFlightsList';
import { HoldingFlightsList } from './HoldingFlightsList';
import { PositionUpdatedFlightList } from './PositionUpdatedFlightList';
import { TaxiTimesFlightsList } from './TaxiTimesFlightsList';
const EMPTY_CUSTOM_LAYOUTS: SavedDashboardLayout[] = [];

interface DragHandleIconProps {
    label: string;
}
export interface DashboardWidget {
    id: string;
    label: string;
    component: React.ReactNode;
    enabled: boolean;
    layout: GridItem;
}
const WIDGET_DEFINITIONS: DashboardWidget[] = [
    {
        id: 'holding',
        label: 'Holding Flights',
        component: <HoldingFlightsList />,
        enabled: true,
        layout: {
            i: 'holding',
            x: 0,
            y: 6,
            w: 6,
            h: 4,
            minW: 3,
            minH: 3
        }
    },
    {
        id: 'diverting',
        label: 'Diverting Flights',
        component: <DivertingFlightsList />,
        enabled: true,
        layout: {
            i: 'diverting',
            x: 6,
            y: 6,
            w: 6,
            h: 4,
            minW: 3,
            minH: 3
        }
    },
    {
        id: 'emergency',
        label: 'Emergency Flights',
        component: <EmergencyFlightsList />,
        enabled: true,
        layout: {
            i: 'emergency',
            x: 0,
            y: 10,
            w: 6,
            h: 3,
            minW: 3,
            minH: 3
        }
    },
    {
        id: 'goAround',
        label: 'Go-Around Flights',
        component: <GoAroundFlightsList />,
        enabled: true,
        layout: {
            i: 'goAround',
            x: 6,
            y: 10,
            w: 6,
            h: 3,
            minW: 3,
            minH: 3
        }
    },
    {
        id: 'taxiTimes',
        label: 'Taxi Times',
        component: <TaxiTimesFlightsList />,
        enabled: true,
        layout: {
            i: 'taxiTimes',
            x: 0,
            y: 0,
            w: 4,
            h: 6,
            minW: 3,
            minH: 3
        }
    },
    {
        id: 'flightStatsChart',
        label: 'Flights by Monitor Status',
        component: <FlightStatsChart />,
        enabled: true,
        layout: {
            i: 'flightStatsChart',
            x: 9,
            y: 0,
            w: 3,
            h: 6,
            minW: 3,
            minH: 6
        }
    },
    {
        id: 'positionUpdated',
        label: 'Position Update Overdue',
        component: <PositionUpdatedFlightList />,
        enabled: true,
        layout: {
            i: 'positionUpdated',
            x: 4,
            y: 0,
            w: 5,
            h: 6,
            minW: 3,
            minH: 3
        }
    }
];
export const INITIAL_WIDGETS = applyDashboardLayout(WIDGET_DEFINITIONS, DEFAULT_DASHBOARD_LAYOUT);

const DragHandleIcon = ({ label }: DragHandleIconProps) => {
    return (
        <Button type="button" svgIcon={DragOutline} className="grid-item-drag-icon" aria-label={`Drag ${label}`} title={`Drag ${label}`} />
    );
};

const Dashboard = () => {
    const [showSettings, setShowSettings] = useState(false);
    useEvent('dashboard-settings-open', () => setShowSettings(true));
    const hideEmptyWidgets = useAppSelector((x) => x.settings.hideEmptyDashboardWidgets);
    const userEditingLayout = useRef(false);
    const finishEditTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        const finish = () => {
            if (finishEditTimer.current !== null) clearTimeout(finishEditTimer.current);
            // RGL commits its final layout during mouseup/touchend. Keep the flag
            // until those handlers have run, then ignore passive layout callbacks.
            finishEditTimer.current = setTimeout(() => {
                userEditingLayout.current = false;
            }, 0);
        };
        window.addEventListener('mouseup', finish);
        window.addEventListener('touchend', finish);
        window.addEventListener('touchcancel', finish);
        return () => {
            window.removeEventListener('mouseup', finish);
            window.removeEventListener('touchend', finish);
            window.removeEventListener('touchcancel', finish);
            if (finishEditTimer.current !== null) clearTimeout(finishEditTimer.current);
        };
    }, []);
    const beginLayoutEdit = (event: React.SyntheticEvent) => {
        const target = event.target;
        if (target instanceof Element && target.closest('.grid-item-drag-icon, .react-resizable-handle')) {
            if (finishEditTimer.current !== null) clearTimeout(finishEditTimer.current);
            userEditingLayout.current = true;
        }
    };
    const focusedWorkspaceId = useAppSelector((state) => state.workspace.selectedId);
    const [countSnapshot, setCountSnapshot] = useState<{ workspaceId: typeof focusedWorkspaceId; counts: number[] } | null>(null);
    const handleCountsChange = useCallback(
        (counts: number[]) => {
            setCountSnapshot((previous) =>
                previous !== null &&
                previous.workspaceId === focusedWorkspaceId &&
                previous.counts.length === counts.length &&
                previous.counts.every((count, index) => count === counts[index])
                    ? previous
                    : { workspaceId: focusedWorkspaceId, counts }
            );
        },
        [focusedWorkspaceId]
    );
    const dispatch = useAppDispatch();
    const savedWidgets = useAppSelector((x) => x.settings.dashboardWidgets);
    const customLayouts = useAppSelector((x) => x.settings.dashboardCustomLayouts) ?? EMPTY_CUSTOM_LAYOUTS;
    const isDark = useAppSelector((x) => x.settings.isDark);
    const widgets = useMemo(() => mergeWidgets(savedWidgets), [savedWidgets]);
    const muiTheme = useMemo(() => createDashboardTheme(isDark), [isDark]);
    const visibleWidgets = useMemo(() => {
        if (!hideEmptyWidgets) return widgets.filter((widget) => widget.enabled);
        const counts = countSnapshot?.workspaceId === focusedWorkspaceId ? countSnapshot.counts : null;
        return getVisibleDashboardWidgets(widgets, counts);
    }, [widgets, hideEmptyWidgets, countSnapshot, focusedWorkspaceId]);
    const gridItems: Layout = useMemo(() => visibleWidgets.map((widget) => widget.layout), [visibleWidgets]);
    const saveWidgets = useCallback(
        (nextWidgets: DashboardWidget[]) => {
            const nextSettings = toWidgetSettings(nextWidgets);
            if (!settingsAreEqual(toWidgetSettings(widgets), nextSettings)) {
                dispatch(setSetting('dashboardWidgets', nextSettings));
            }
        },
        [dispatch, widgets]
    );
    const handleLayoutChange = useCallback(
        (nextLayout: Layout) => {
            if (hideEmptyWidgets && !userEditingLayout.current) return;
            saveWidgets(mergeEditedDashboardLayout(widgets, nextLayout));
        },
        [saveWidgets, widgets, hideEmptyWidgets]
    );
    const handleSettingsSave = (
        newWidgets: DashboardWidget[],
        nextHideEmptyWidgets: boolean,
        nextCustomLayouts: SavedDashboardLayout[]
    ) => {
        saveWidgets(newWidgets);
        dispatch(setSetting('hideEmptyDashboardWidgets', nextHideEmptyWidgets));
        if (JSON.stringify(customLayouts) !== JSON.stringify(nextCustomLayouts)) {
            dispatch(setSetting('dashboardCustomLayouts', nextCustomLayouts));
        }
        setShowSettings(false);
    };
    return (
        <ThemeProvider theme={muiTheme}>
            <div className="dashboard-grid">
                <header className="dashboard-toolbar">
                    <DashboardSummary onCountsChange={handleCountsChange} />
                </header>
                {hideEmptyWidgets && visibleWidgets.length === 0 && (
                    <Box sx={{ m: 2.5, p: 3, textAlign: 'center', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                        <Typography>No records for the enabled widgets.</Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                            Widgets will appear when matching flight data arrives.
                        </Typography>
                    </Box>
                )}
                <div
                    className="dashboard-layout-interaction"
                    style={{ flex: '1 0 0', minHeight: 0, minWidth: 0, width: '100%' }}
                    onMouseDownCapture={beginLayoutEdit}
                    onTouchStartCapture={beginLayoutEdit}>
                    <GridLayout items={gridItems} onLayoutChange={handleLayoutChange} fillHeight>
                        {visibleWidgets.map((widget) => (
                            <div
                                key={widget.id}
                                className={`grid-item dashboard-flight-panel--${
                                    widget.id === 'goAround'
                                        ? 'go-around'
                                        : widget.id === 'taxiTimes'
                                          ? 'taxi-times'
                                          : widget.id === 'flightStatsChart'
                                            ? 'monitored'
                                            : widget.id === 'positionUpdated'
                                              ? 'position-updated'
                                              : widget.id
                                }`}>
                                <DragHandleIcon label={widget.label} />
                                {widget.component}
                            </div>
                        ))}
                    </GridLayout>
                </div>
                {showSettings && (
                    <DashboardSettings
                        widgets={widgets}
                        customLayouts={customLayouts}
                        hideEmptyWidgets={hideEmptyWidgets}
                        counts={countSnapshot?.workspaceId === focusedWorkspaceId ? countSnapshot.counts : null}
                        onClose={() => setShowSettings(false)}
                        onSave={handleSettingsSave}
                    />
                )}
            </div>
        </ThemeProvider>
    );
};
export default Dashboard;
